const normalizeContext = (context) => {
    return {
        identifier: context.identifier || context.name || '',
        name: context.name || context.identifier || '',
        type: context.type || null,
        ulid: context.ulid || null,
    }
}

/**
 * Returns display-safe context objects for a context-selector criteria.
 *
 * When values.data contains identifier strings (the normal API case), it
 * joins them with the richer objects in contexts.data so the type and name
 * are available for display.  If values is empty the function falls back to
 * contexts.data directly.  All returned objects are normalised so the
 * template can always rely on `identifier`, `name`, `type`, and `ulid`.
 *
 * `name` is the label to show for a context: it falls back to the identifier
 * when the context has no name, so templates should render `name` rather than
 * choosing between the two themselves.
 */
export const getContextDisplayValues = (criteria) => {
    const identifiersFromValues = (criteria.values?.data || [])
        .map((value) => value.value)
        .filter((value) => !!value)

    if (identifiersFromValues.length === 0) {
        return (criteria.contexts?.data || []).map(normalizeContext)
    }

    const contextByIdentifier = (criteria.contexts?.data || []).reduce(
        (carry, context) => {
            carry[context.identifier] = context
            return carry
        },
        {}
    )

    return identifiersFromValues.map((identifier) => {
        const existingContext = contextByIdentifier[identifier]

        if (existingContext) {
            return normalizeContext(existingContext)
        }

        return {
            identifier,
            name: identifier,
            type: null,
            ulid: null,
        }
    })
}

/**
 * Returns display-safe attribute value objects for an attribute-selector
 * criteria.  Each entry is normalised to `{ value, key }` so the template
 * does not need to worry about whether values arrived as plain strings or
 * `{ value }` objects.
 */
export const getAttributeDisplayValues = (criteria) => {
    const values = criteria.values?.data || []
    return values.map((value, index) => ({
        value: value.value || value,
        key: `attr-${index}`,
    }))
}

/**
 * Returns the segment list for a segment-selector criteria, falling back to
 * an empty array when segments are absent.
 */
export const getSegmentDisplayValues = (criteria) => {
    return criteria.segments?.data || []
}

/**
 * Returns the human-readable display label for a criteria comparer value.
 */
export const formatComparer = (value) => {
    switch (value) {
        case 'equal':
            return '=='
        case 'notequal':
            return '!='
        case 'in':
            return 'IS IN'
        case 'notin':
            return 'IS NOT IN'
        case 'gt':
            return '>'
        case 'gte':
            return '>='
        case 'lt':
            return '<'
        case 'lte':
            return '<='
        case 'contains':
            return 'CONTAINS'
        case 'notcontains':
            return 'DOES NOT CONTAIN'
        case 'startswith':
            return 'STARTS WITH'
        case 'notstartswith':
            return 'DOES NOT START WITH'
        case 'endswith':
            return 'ENDS WITH'
        case 'notendswith':
            return 'DOES NOT END WITH'
        case 'isnull':
            return 'IS NULL'
        case 'notnull':
            return 'IS NOT NULL'
        default:
            return '(Unknown)'
    }
}
